import { useState, useRef, useCallback } from 'react';
import { Loader2, RefreshCw, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { diagnosticsAPI } from '../../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';

const MAX_DISPLAY_ROWS = 50;

export function SmoobuCancellationBackfillPanel() {
  const [running, setRunning] = useState(false);
  const [confirmApply, setConfirmApply] = useState(false);

  // Accumulated progress across batches
  const [progress, setProgress] = useState(null);
  const [results, setResults] = useState(null);

  // Allow cancellation of the loop
  const cancelledRef = useRef(false);

  const run = useCallback(async (dryRun) => {
    setRunning(true);
    setResults(null);
    setConfirmApply(false);
    cancelledRef.current = false;

    let cursor = null;
    let totalProcessed = 0;
    let totalMismatches = 0;
    let totalUpdates = 0;
    let totalApiErrors = 0;
    let totalFetchFailed = 0;
    let totalRecords = 0;
    const allSamples = [];
    const allFailedIds = [];

    setProgress({ processed: 0, total: 0, mismatches: 0, updates: 0, apiErrors: 0, fetchFailed: 0, done: false });

    try {
      while (true) {
        if (cancelledRef.current) break;

        const { data } = await diagnosticsAPI.smoobuBackfillCancellationStatus({
          dryRun, cursor, batchSize: 20,
        });

        totalProcessed += data.processed_count;
        totalMismatches += data.mismatches_found;
        totalUpdates += data.updates_applied;
        totalApiErrors += data.api_errors;
        totalFetchFailed += (data.fetch_failed_ids?.length || 0);
        totalRecords = data.total_records;

        if (data.sample?.length) {
          for (const s of data.sample) {
            allSamples.push(s);
          }
        }
        if (data.fetch_failed_ids?.length) {
          for (const id of data.fetch_failed_ids) {
            allFailedIds.push(id);
          }
        }

        setProgress({
          processed: totalProcessed,
          total: totalRecords,
          mismatches: totalMismatches,
          updates: totalUpdates,
          apiErrors: totalApiErrors,
          fetchFailed: totalFetchFailed,
          done: data.done,
          lastCursor: data.next_cursor,
        });

        if (data.done) break;
        cursor = data.next_cursor;
      }

      const final = {
        dry_run: dryRun,
        total_scanned: totalProcessed,
        total_mismatches: totalMismatches,
        total_updated: totalUpdates,
        api_errors: totalApiErrors,
        fetch_failed: totalFetchFailed,
        fetch_failed_ids: allFailedIds,
        sample: allSamples,
        cancelled: cancelledRef.current,
      };
      setResults(final);

      if (!dryRun && totalUpdates > 0) {
        toast.success(`Backfill applied: ${totalUpdates} records corrected`);
      }
    } catch (err) {
      // Surface how far we got so the operator can see partial progress
      setResults({
        dry_run: dryRun,
        total_scanned: totalProcessed,
        total_mismatches: totalMismatches,
        total_updated: totalUpdates,
        api_errors: totalApiErrors,
        fetch_failed: totalFetchFailed,
        fetch_failed_ids: allFailedIds,
        sample: allSamples,
        error: getErrorMessage(err),
        last_cursor: cursor,
      });
      toast.error(getErrorMessage(err));
    } finally {
      setRunning(false);
    }
  }, []);

  const cancel = useCallback(() => {
    cancelledRef.current = true;
  }, []);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <RefreshCw className="h-5 w-5 text-blue-500" />
          Smoobu Cancellation Backfill
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground">
          One-shot reconciliation: fetches every Smoobu reservation from the API,
          marks cancelled bookings correctly, zeroes their revenue. Dry-run by default.
          Processes in batches of 20.
        </p>

        <div className="flex gap-2">
          <Button onClick={() => run(true)} disabled={running}>
            {running ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <RefreshCw className="h-4 w-4 mr-2" />
            )}
            Dry Run
          </Button>

          {!confirmApply ? (
            <Button
              variant="destructive"
              onClick={() => setConfirmApply(true)}
              disabled={running}
            >
              Apply
            </Button>
          ) : (
            <Button
              variant="destructive"
              onClick={() => run(false)}
              disabled={running}
            >
              {running ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <AlertTriangle className="h-4 w-4 mr-2" />
              )}
              Confirm Apply
            </Button>
          )}

          {running && (
            <Button variant="outline" onClick={cancel}>
              Stop
            </Button>
          )}
        </div>

        {/* Live progress while running */}
        {running && progress && (
          <div className="text-sm text-muted-foreground">
            Processed {progress.processed}{progress.total ? ` / ${progress.total}` : ''}...{' '}
            {progress.mismatches > 0 && (
              <span className="text-amber-600 dark:text-amber-400">
                {progress.mismatches} mismatch{progress.mismatches !== 1 ? 'es' : ''} found
              </span>
            )}
            {progress.updates > 0 && (
              <span className="text-green-600 dark:text-green-400 ml-2">
                {progress.updates} updated
              </span>
            )}
            {progress.fetchFailed > 0 && (
              <span className="text-red-600 dark:text-red-400 ml-2">
                {progress.fetchFailed} fetch failed
              </span>
            )}
          </div>
        )}

        {/* Final results */}
        {results && (
          <div className="space-y-4">
            {results.error && (
              <div className="text-sm text-red-600 dark:text-red-400 border border-red-200 dark:border-red-800 rounded-lg p-3">
                Error: {results.error}
                {results.last_cursor && (
                  <span className="block mt-1 text-xs text-muted-foreground">
                    Last cursor: {results.last_cursor} (restart is safe, updates are idempotent)
                  </span>
                )}
              </div>
            )}

            {results.cancelled && (
              <div className="text-sm text-amber-600 dark:text-amber-400">
                Stopped by user after {results.total_scanned} records.
              </div>
            )}

            {/* Summary counters */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div className="rounded-lg border p-3">
                <p className="text-xs text-muted-foreground">Total scanned</p>
                <p className="text-xl font-bold tabular-nums">{results.total_scanned}</p>
              </div>
              <div className="rounded-lg border p-3">
                <p className="text-xs text-muted-foreground">
                  {results.dry_run ? 'Mismatches' : 'Updated'}
                </p>
                <p className="text-xl font-bold tabular-nums">
                  {results.dry_run ? results.total_mismatches : results.total_updated}
                </p>
              </div>
              <div className="rounded-lg border p-3">
                <p className="text-xs text-muted-foreground">Fetch failed</p>
                <p className="text-xl font-bold tabular-nums">{results.fetch_failed || 0}</p>
              </div>
              <div className="rounded-lg border p-3">
                <p className="text-xs text-muted-foreground">Mode</p>
                <Badge variant={results.dry_run ? 'secondary' : 'default'}>
                  {results.dry_run ? 'Dry run' : 'Applied'}
                </Badge>
              </div>
            </div>

            {!results.dry_run && results.total_updated > 0 && !results.error && (
              <div className="flex items-center gap-2 text-sm text-green-600 dark:text-green-400">
                <CheckCircle2 className="h-4 w-4" />
                Backfill complete. Re-run dry run to verify zero remaining mismatches.
              </div>
            )}

            {/* Sample table */}
            {results.sample?.length > 0 && (
              <div className="space-y-1">
                <p className="text-sm font-medium">
                  {results.sample.length > MAX_DISPLAY_ROWS
                    ? `Showing ${MAX_DISPLAY_ROWS} of ${results.sample.length} mismatches`
                    : `${results.sample.length} mismatch${results.sample.length !== 1 ? 'es' : ''}`}
                </p>
                <div className="overflow-x-auto border rounded-lg">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b bg-muted/50">
                        <th className="text-left px-3 py-2 font-medium">Smoobu ID</th>
                        <th className="text-left px-3 py-2 font-medium">Guest</th>
                        <th className="text-left px-3 py-2 font-medium">Check-in</th>
                        <th className="text-left px-3 py-2 font-medium">Old Status</th>
                        <th className="text-left px-3 py-2 font-medium">New Status</th>
                        <th className="text-right px-3 py-2 font-medium">Revenue Change</th>
                      </tr>
                    </thead>
                    <tbody>
                      {results.sample.slice(0, MAX_DISPLAY_ROWS).map((r, i) => (
                        <tr key={r.smoobu_id || i} className="border-b last:border-0">
                          <td className="px-3 py-2 font-mono text-xs">{r.smoobu_id}</td>
                          <td className="px-3 py-2">{r.guest_name}</td>
                          <td className="px-3 py-2 font-mono text-xs">{r.check_in}</td>
                          <td className="px-3 py-2">
                            <Badge variant="secondary">{r.old_status}</Badge>
                          </td>
                          <td className="px-3 py-2">
                            <Badge variant="destructive">{r.new_status}</Badge>
                          </td>
                          <td className="px-3 py-2 text-right tabular-nums text-red-600 dark:text-red-400">
                            {r.net_revenue_before != null
                              ? `${Number(r.net_revenue_before).toFixed(2)} → ${Number(r.net_revenue_after).toFixed(2)}`
                              : '(null) → 0'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
